import React from "react";
import { motion } from "framer-motion";
import {
  Leaf,
  Users,
  Scale,
  Globe,
  BarChart3,
  ShieldCheck
} from "lucide-react";
import Navbar from "../../components/Navbar";

const esgPillars = [
  {
    title: "Environmental Responsibility",
    desc: "Reducing environmental impact through sustainable resource use, emissions control, and environmental management systems.",
    icon: Leaf
  },
  {
    title: "Social Responsibility",
    desc: "Ensuring employee welfare, community engagement, human rights, and ethical labor practices.",
    icon: Users
  },
  {
    title: "Governance & Ethics",
    desc: "Strengthening transparency, accountability, compliance, and ethical decision-making frameworks.",
    icon: Scale
  }
];

const esgServices = [
  {
    title: "ESG Strategy & Framework Development",
    icon: Globe
  },
  {
    title: "Sustainability Risk Assessment",
    icon: ShieldCheck
  },
  {
    title: "ESG Reporting & Performance Metrics",
    icon: BarChart3
  }
];

const SustainabilityESG = () => {
  return (
    <>
    <Navbar />
    <section className="bg-white py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            Sustainability & ESG
          </h2>
          <div className="w-24 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions supports organizations in embedding sustainability
            and ESG principles into business strategies to ensure long-term
            growth, resilience, and stakeholder trust.
          </p>
        </motion.div>

        {/* ================= ESG PILLARS ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-24">
          {esgPillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
                whileHover={{
                  y: -12,
                  scale: 1.04,
                  boxShadow: "0px 25px 50px rgba(34,197,94,0.25)"
                }}
                className="relative bg-gray-50 rounded-3xl p-8 text-center border border-gray-100 shadow-lg group"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[var(--bg-primary)] text-white flex items-center justify-center mb-6 shadow-md">
                  <Icon size={28} />
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {pillar.title}
                </h3>

                <p className="text-sm text-gray-600 leading-relaxed">
                  {pillar.desc}
                </p>

                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            );
          })}
        </div>

        {/* ================= IMPACT FLOW ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* LEFT – Narrative */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Driving Sustainable Business Impact
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6">
              Sustainability and ESG are no longer optional. Organizations must
              demonstrate environmental stewardship, social responsibility,
              and strong governance to meet regulatory expectations and
              stakeholder demands.
            </p>

            <p className="text-gray-600 leading-relaxed">
              Our ESG consulting services help organizations integrate
              sustainability into operations, enhance ESG performance, and
              build transparent reporting mechanisms aligned with
              international best practices.
            </p>
          </motion.div>

          {/* RIGHT – ESG SERVICES STACK */}
          <div className="space-y-6">
            {esgServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0px 20px 40px rgba(16,185,129,0.25)"
                  }}
                  className="flex items-center gap-6 bg-white p-6 rounded-2xl shadow-md border border-gray-100"
                >
                  <div className="w-14 h-14 rounded-xl bg-[var(--bg-primary)] text-white flex items-center justify-center shadow-md">
                    <Icon size={24} />
                  </div>
                  <h4 className="text-base font-semibold text-gray-900">
                    {service.title}
                  </h4>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ================= CTA ================= */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-24 text-center"
        >
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.97 }}
            className="px-10 py-4 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            Build a Sustainable Future
          </motion.button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default SustainabilityESG;
